import React, { useMemo } from 'react';
import { RouteProp } from '@react-navigation/native';
import { ProfilesTabParamList, useGoBack } from '../../../../../utils/appNavigationNames';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import ClientInvitationProvider, { ClientInvitationValue } from '../../../../shared/ClientInvitationProvider';
import OrganizationClientInvitationFields, {
    clientInvitationToFormValue,
} from '../../../../shared/OrganizationClientInvitations/OrganizationClientInvitationFields';
import useOrganizationClientInvitations from '../../../../../hooks/api/useOrganizationClientInvitations';
import { Form } from '../../../../v2BaseComponents/Form';
import { getIntl } from '../../../../../utils/intl';
import MaybePublicOrgMissingInfoAlert from '../../../../shared/MaybePublicOrgMissingInfoAlert';
import useOrganizationInvitationDefinitions from '../../../../../hooks/api/useOrganizationInvitationDefinitions';

const intl = getIntl('profiles.editClientInvitation', {
    update: 'Update',
});

export type EditClientInvitationScreenOwnProps = {};

export type EditClientInvitationScreenNavigatorProps = {
    route: RouteProp<ProfilesTabParamList, 'edit-client-invitation'>;
};

export type EditClientInvitationScreenProps = EditClientInvitationScreenOwnProps &
    EditClientInvitationScreenNavigatorProps;

const Content: React.FC<ClientInvitationValue> = ({ clientInvitation, clientRecord }) => {
    const goBack = useGoBack();
    const initialValues = useMemo(
        () => clientInvitationToFormValue(clientInvitation, !!clientRecord.provisionedMedicalProfileId),
        [clientInvitation, clientRecord],
    );
    const { updateClientInvitation, isUpdatingClientInvitation } = useOrganizationClientInvitations({
        onUpdateInvitation: goBack,
    });
    const { isLoadingInvitationDefinitions, invitationDefinitions } = useOrganizationInvitationDefinitions({});

    return (
        <AnimateLoadedContent isLoading={isUpdatingClientInvitation || isLoadingInvitationDefinitions}>
            {() => (
                <Form
                    fillContainer
                    actionContainerSubmit
                    initialValues={initialValues}
                    onSubmit={(values) =>
                        updateClientInvitation({
                            invitationId: clientInvitation.organizationClientInvitationId,
                            update: {
                                ...values,
                                expirationDateTime: values.expirationDateTime
                                    ? new Date(values.expirationDateTime).toISOString()
                                    : undefined,
                            },
                        })
                    }
                    onCancel={goBack}
                    saveText={intl('update')}
                    saveIcon="CheckCircle"
                >
                    {({ values }) => (
                        <>
                            <MaybePublicOrgMissingInfoAlert />
                            <OrganizationClientInvitationFields
                                invitationDefinitions={invitationDefinitions}
                                value={values}
                            />
                        </>
                    )}
                </Form>
            )}
        </AnimateLoadedContent>
    );
};

const EditClientInvitationScreen: React.FC<EditClientInvitationScreenProps> = ({ route }) => {
    return (
        <ClientInvitationProvider clientRecordId={route.params.clientRecordId}>
            {({ clientInvitation, clientRecord }) => (
                <Content clientInvitation={clientInvitation} clientRecord={clientRecord} />
            )}
        </ClientInvitationProvider>
    );
};

export default React.memo(EditClientInvitationScreen);
